import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Siren, 
  Map,
  Users, 
  ShieldCheck,
  BadgeDollarSign, 
  Receipt, 
  FileBarChart, 
  Settings,
  LogOut
} from 'lucide-react';

const ALL_NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'operator', 'accounts'] },
  { to: '/occurrence-book', icon: Siren, label: 'Occurrence Book', roles: ['admin', 'operator', 'accounts'] },
  { to: '/map-view', icon: Map, label: 'Map View', roles: ['admin', 'operator'] },
  { to: '/customers', icon: Users, label: 'Customers', roles: ['admin', 'operator', 'accounts'] },
  { to: '/contractors', icon: ShieldCheck, label: 'Contractors', roles: ['admin', 'operator', 'accounts'] },
  { to: '/pricing', icon: BadgeDollarSign, label: 'Pricing', roles: ['admin', 'accounts'] },
  { to: '/billing', icon: Receipt, label: 'Billing', roles: ['admin', 'accounts'] },
  { to: '/reports', icon: FileBarChart, label: 'Reports', roles: ['admin', 'operator', 'accounts'] },
  { to: '/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const role = user?.role || 'operator';
  const navItems = ALL_NAV_ITEMS.filter((item) => item.roles.includes(role));
  const initials = (user?.name || 'CSG').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const roleLabel = { admin: 'Administrator', operator: 'Control Room', accounts: 'Accounts' }[role] || role;

  return (
    <aside className="w-64 bg-white border-r border-slate-200 h-screen flex flex-col" data-testid="sidebar">
      {/* Logo Section */}
      <div className="h-16 flex items-center px-6 border-b border-slate-200">
        <img src="/csg-logo.png" alt="CSG Security" className="h-9 w-auto" data-testid="sidebar-logo" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-[#0D5B69]/8 text-[#0D5B69] border-l-[3px] border-[#80C030] pl-[9px]'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
            <span className="text-sm font-semibold text-slate-600">{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name || '—'}</p>
            <p className="text-xs text-slate-500">{roleLabel}</p>
          </div>
          <button
            onClick={logout}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors"
            title="Sign out"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>
    </aside>
  );
};
